import axios from 'axios'
import { resolveFrontendApiBase } from './apiBase';

export interface PlanInfo {
  id: string
  planName: string
  price: number
  stripePriceID: string
  stripeProductID: string
  tier?: number
  monthlyCredits?: number
  monthlyEquivalentCredits?: number
  creditsPerInterval?: number
  intervalMonths?: number
  billingInterval?: string
  planKey?: string
  oneTimeCredits?: number
  isForeverCreditPlan?: boolean
  metadata?: Record<string, any>
}

interface PlansResponse {
  subscriptionPlans: PlanInfo[]
  foreverCreditPlans: any[]
}

const API_BASE = resolveFrontendApiBase()

let cachedPlans: PlanInfo[] | null = null
let fetchingPlans: Promise<PlanInfo[] | null> | null = null

const toNumber = (value: any): number | null => {
  if (value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const coercePositive = (value: any): number | null => {
  const n = toNumber(value)
  if (n === null || n <= 0) return null
  return n
}

const firstPositive = (...values: any[]): number | null => {
  for (const value of values) {
    const n = coercePositive(value)
    if (n !== null) return n
  }
  return null
}

const normalizePlan = (raw: any, planType: 'subscription' | 'forever' = 'subscription'): PlanInfo | null => {
  if (!raw) return null

  const metadata = typeof raw.metadata === 'object' && raw.metadata !== null ? raw.metadata : {}

  const planKey: string | undefined = (raw.planKey ?? raw.plan_name ?? raw.planName) && String(raw.planKey ?? raw.plan_name ?? raw.planName)
  const id: string = String(raw.id ?? raw.docId ?? raw.documentId ?? raw.documentID ?? planKey ?? '')
  if (!id) return null

  const planName = String(raw.planName ?? raw.displayName ?? metadata.displayName ?? planKey ?? id)
  const price = toNumber(raw.price ?? raw.amount ?? metadata.price) ?? 0
  const billingIntervalRaw = String(raw.billingInterval ?? raw.interval ?? metadata.billingInterval ?? 'monthly').toLowerCase()
  const billingInterval = billingIntervalRaw.startsWith('year') || billingIntervalRaw.startsWith('annual') ? 'yearly' : 'monthly'
  const intervalMonths =
    coercePositive(raw.intervalMonths ?? metadata.intervalMonths) ?? (billingInterval === 'yearly' ? 12 : 1)

  const oneTimeCredits = firstPositive(raw.oneTimeCredits, raw.credits, metadata.oneTimeCredits, metadata.credits)

  let creditsPerInterval =
    firstPositive(raw.creditsPerInterval, metadata.creditsPerInterval, raw.monthlyCredits, metadata.monthlyCredits) ?? null

  let monthlyEquivalentCredits =
    firstPositive(
      raw.monthlyEquivalentCredits,
      metadata.monthlyEquivalentCredits,
      metadata.monthlyCredits,
      raw.annualCredits ? Number(raw.annualCredits) / intervalMonths : null,
      metadata.annualCredits ? Number(metadata.annualCredits) / intervalMonths : null,
    ) ?? null

  if (!monthlyEquivalentCredits && creditsPerInterval) {
    monthlyEquivalentCredits = creditsPerInterval / intervalMonths
  }

  if (!creditsPerInterval && monthlyEquivalentCredits) {
    creditsPerInterval = monthlyEquivalentCredits * intervalMonths
  }

  if (!creditsPerInterval && metadata.annualCredits) {
    const annual = coercePositive(metadata.annualCredits)
    if (annual) {
      creditsPerInterval = annual
      monthlyEquivalentCredits = annual / intervalMonths
    }
  }

  const fallbackMonthly = firstPositive(raw.monthlyCredits, metadata.monthlyCredits)
  if (!monthlyEquivalentCredits && fallbackMonthly) {
    monthlyEquivalentCredits = fallbackMonthly
  }

  if (planType === 'forever' && oneTimeCredits) {
    creditsPerInterval = oneTimeCredits
    monthlyEquivalentCredits = oneTimeCredits
  }

  const stripePrice = raw.stripePriceID ?? raw.stripePriceId ?? metadata.priceID ?? ''
  const stripeProduct = raw.stripeProductID ?? raw.stripeProductId ?? metadata.productID ?? ''

  return {
    id,
    planName,
    planKey,
    price,
    stripePriceID: stripePrice ? String(stripePrice) : '',
    stripeProductID: stripeProduct ? String(stripeProduct) : '',
    billingInterval: planType === 'forever' ? 'monthly' : billingInterval,
    intervalMonths: planType === 'forever' ? 1 : intervalMonths,
    creditsPerInterval: creditsPerInterval ?? oneTimeCredits ?? undefined,
    monthlyEquivalentCredits: monthlyEquivalentCredits ?? oneTimeCredits ?? undefined,
    monthlyCredits: planType === 'forever' ? oneTimeCredits ?? undefined : fallbackMonthly ?? monthlyEquivalentCredits ?? undefined,
    metadata,
    oneTimeCredits: oneTimeCredits ?? undefined,
    isForeverCreditPlan: planType === 'forever',
  }
}

async function fetchPlans(): Promise<PlanInfo[] | null> {
  try {
    const { data } = await axios.get<PlansResponse>(`${API_BASE}/get-subscription-plans`, {
      params: {
        includeLegacy: true,
        includeInactive: true,
      },
    })
    const rawPlans = Array.isArray(data.subscriptionPlans) ? data.subscriptionPlans : []
    const rawForeverPlans = Array.isArray(data.foreverCreditPlans) ? data.foreverCreditPlans : []
    const normalized = [...rawPlans.map((plan) => normalizePlan(plan, 'subscription')), ...rawForeverPlans.map((plan) => normalizePlan(plan, 'forever'))]
      .filter((plan): plan is PlanInfo => Boolean(plan))
    return normalized
  } catch (_e) {
    return null
  }
}

export async function getPlansCached(): Promise<PlanInfo[] | null> {
  if (cachedPlans) return cachedPlans
  if (!fetchingPlans) fetchingPlans = fetchPlans()
  cachedPlans = await fetchingPlans
  fetchingPlans = null
  return cachedPlans
}

export function shouldShowDollarEquivalents(): boolean {
  // Temporary product decision: hide all USD-equivalent estimates in UI
  // while preserving pricing helpers for future re-enable.
  return false

  /*
  try {
    const raw = localStorage.getItem('showDollarEquivalents')
    if (raw == null) return true
    return String(raw) !== 'false'
  } catch (_e) {
    return true
  }
  */
}

const normalizePlanId = (value?: string): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : ''
const isNoSubscriptionPlanId = (value?: string): boolean => {
  const normalized = normalizePlanId(value)
  return normalized === '' || normalized === 'poetrial'
}

const average = (values: number[]): number => values.reduce((sum, value) => sum + value, 0) / values.length

export async function estimateDollarPerCredit(planID?: string): Promise<number> {
  const plans = await getPlansCached()
  if (plans && plans.length > 0) {
    const matchPlanById = (target?: string): PlanInfo | undefined => {
      if (!target) return undefined
      const norm = String(target).trim().toLowerCase()
      return plans.find(plan => {
        const candidates = [
          plan.stripeProductID,
          plan.stripePriceID,
          plan.id,
          plan.planKey,
          plan.planName,
          plan.metadata?.planID,
          plan.metadata?.productID,
          plan.metadata?.priceID,
          plan.metadata?.planKey,
          plan.metadata?.planGroupKey,
        ]
          .filter(Boolean)
          .map(value => String(value).trim().toLowerCase())
        return candidates.includes(norm)
      })
    }

    const rate = (plan?: PlanInfo): number | null => {
      if (!plan) return null
      const price = Number(plan.price) || 0
      if (!(price > 0)) return null

      const intervalMonths =
        Number(plan.intervalMonths) && Number(plan.intervalMonths) > 0
          ? Number(plan.intervalMonths)
          : (() => {
              const interval = String(plan.billingInterval || '').toLowerCase()
              if (interval === 'annual' || interval === 'yearly' || interval === 'year') return 12
              return 1
            })()

      const monthlyCreditsCandidates = [
        plan.monthlyCredits,
        plan.monthlyEquivalentCredits,
        plan.metadata?.monthlyCredits,
        plan.metadata?.monthlyEquivalentCredits,
        plan.metadata?.annualCredits ? Number(plan.metadata.annualCredits) / intervalMonths : undefined,
      ]
        .map(candidate => Number(candidate))
        .filter(value => Number.isFinite(value) && value > 0)

      let monthlyCredits: number | null =
        monthlyCreditsCandidates.length > 0 ? monthlyCreditsCandidates[0] : null

      if (!monthlyCredits) {
        const creditsPerInterval = Number(plan.creditsPerInterval)
        if (Number.isFinite(creditsPerInterval) && creditsPerInterval > 0) {
          monthlyCredits = creditsPerInterval / intervalMonths
        } else if (Number.isFinite(plan.metadata?.annualCredits) && Number(plan.metadata?.annualCredits) > 0) {
          monthlyCredits = Number(plan.metadata?.annualCredits) / intervalMonths
        }
      }

      if (!monthlyCredits || monthlyCredits <= 0) return null
      const monthlyPrice = price / intervalMonths
      return monthlyPrice / monthlyCredits
    }

    const planRate = rate(matchPlanById(planID))
    if (planRate != null) return planRate

    const subscriptionRates = plans
      .filter((plan) => !plan.isForeverCreditPlan)
      .map(rate)
      .filter((x): x is number => typeof x === 'number' && isFinite(x) && x > 0)
    const foreverRates = plans
      .filter((plan) => plan.isForeverCreditPlan)
      .map(rate)
      .filter((x): x is number => typeof x === 'number' && isFinite(x) && x > 0)

    if (isNoSubscriptionPlanId(planID)) {
      if (foreverRates.length > 0) return average(foreverRates)
      if (subscriptionRates.length > 0) return average(subscriptionRates)
    } else {
      if (subscriptionRates.length > 0) return average(subscriptionRates)
      if (foreverRates.length > 0) return average(foreverRates)
    }
  }
  // Final fallback constant (very small per-credit cost); adjust if pricing changes
  return 0.00004
}

export async function estimateUSD(credits: number, planID?: string): Promise<number> {
  if (!isFinite(credits) || credits <= 0) return 0
  const per = await estimateDollarPerCredit(planID)
  return credits * per
}

export function formatUSD(amount: number): string {
  const n = Math.max(0, amount)
  return `≈ $${n.toFixed(2)}`
}

export function __resetCostUtilsCacheForTests(): void {
  cachedPlans = null
  fetchingPlans = null
}
